import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface EditorPrefs {
  fontSize: number;
  tabSize: number;
  fontFamily: string;
  theme: "dark" | "light" | "system";
}

const DEFAULTS: EditorPrefs = {
  fontSize: 14,
  tabSize: 2,
  fontFamily: "JetBrains Mono",
  theme: "dark",
};

function load(): EditorPrefs {
  return {
    fontSize: parseInt(localStorage.getItem("cc-font-size") || String(DEFAULTS.fontSize)),
    tabSize: parseInt(localStorage.getItem("cc-tab-size") || String(DEFAULTS.tabSize)),
    fontFamily: localStorage.getItem("cc-editor-font") || DEFAULTS.fontFamily,
    theme: (localStorage.getItem("cc-theme") as EditorPrefs["theme"]) || DEFAULTS.theme,
  };
}

function resolveTheme(theme: EditorPrefs["theme"]): "dark" | "light" {
  if (theme === "system") {
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  }
  return theme;
}

function applyThemeToDOM(theme: EditorPrefs["theme"]) {
  const resolved = resolveTheme(theme);
  document.documentElement.classList.toggle("light", resolved === "light");
  document.documentElement.classList.toggle("dark", resolved === "dark");
}

// Singleton listeners
const listeners = new Set<() => void>();
let currentPrefs = load();

// Apply on module load
applyThemeToDOM(currentPrefs.theme);

// Listen for system theme changes
if (typeof window !== "undefined") {
  window.matchMedia("(prefers-color-scheme: light)").addEventListener("change", () => {
    if (currentPrefs.theme === "system") {
      applyThemeToDOM("system");
    }
  });
}

function notify() {
  listeners.forEach((fn) => fn());
}

/** Load theme from DB profile (fires once per session on mount) */
export async function syncThemeFromProfile() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase
      .from("profiles")
      .select("theme")
      .eq("user_id", user.id)
      .single();
    if (data?.theme && data.theme !== currentPrefs.theme) {
      const theme = data.theme as EditorPrefs["theme"];
      currentPrefs = { ...currentPrefs, theme };
      localStorage.setItem("cc-theme", theme);
      applyThemeToDOM(theme);
      notify();
    }
  } catch {
    // Fail silently — localStorage fallback is already applied
  }
}

export function useEditorPrefs() {
  const [prefs, setPrefs] = useState<EditorPrefs>(currentPrefs);

  useEffect(() => {
    const handler = () => setPrefs({ ...currentPrefs });
    listeners.add(handler);
    return () => { listeners.delete(handler); };
  }, []);

  const updatePref = useCallback(<K extends keyof EditorPrefs>(key: K, value: EditorPrefs[K]) => {
    currentPrefs = { ...currentPrefs, [key]: value };
    const storageKey = key === "fontSize" ? "cc-font-size"
      : key === "tabSize" ? "cc-tab-size"
      : key === "fontFamily" ? "cc-editor-font"
      : "cc-theme";
    localStorage.setItem(storageKey, String(value));
    if (key === "theme") {
      applyThemeToDOM(value as EditorPrefs["theme"]);
      // Persist theme to DB profile asynchronously
      supabase.auth.getUser().then(({ data: { user } }) => {
        if (!user) return;
        supabase.from("profiles").update({ theme: String(value) }).eq("user_id", user.id);
      });
    }
    notify();
  }, []);

  return { prefs, updatePref };
}
